import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useUser } from "src/atoms/user";
import { merchantSettingsAtom } from "src/components/FeatureFlagProvider/store/atoms";
import { getCdtStudioAvailability } from "./isCdtStudioEnabled";
import {
  getStudioCategoryConfiguration,
  type StudioCategoryIdentity,
} from "./studioCategoryConfiguration";

type AvailableStores = ResponseMerchFlowApplyFilters["filter_config"] | null;

const categoryIdentityOf = (
  availableStores: AvailableStores,
  merchantId: number,
  merchantName: string,
): StudioCategoryIdentity | undefined => {
  const identities = new Map<string, StudioCategoryIdentity>();
  availableStores?.stores.forEach(({ category_code, retailer_category_code }) => {
    const identity = {
      merchantId,
      merchantName,
      // The page asks before it knows the stores carry a category at all.
      categoryGroup: retailer_category_code?.trim() ?? "",
      categoryCode: category_code?.trim() ?? "",
    };
    if (!identity.categoryGroup || !identity.categoryCode) return;
    const key = `${identity.categoryGroup.toLocaleUpperCase()}\u0000${identity.categoryCode.toLocaleUpperCase()}`;
    identities.set(key, identity);
  });
  return identities.size === 1 ? identities.values().next().value : undefined;
};

/** One answer to "can the studio open here" for the side panel and the page around it. */
export const useCdtStudioAvailability = ({
  isPog,
  merchflowId,
  availableStores,
  hasUnsupportedFixtures,
}: {
  isPog: boolean;
  merchflowId?: number;
  availableStores: AvailableStores;
  /** The planogram has fixtures the studio cannot place products on. */
  hasUnsupportedFixtures?: boolean;
}) => {
  const { user } = useUser();
  const categoryIdentity = useMemo(
    () => categoryIdentityOf(availableStores, user.merchantId, user.merchantName ?? ""),
    [availableStores, user.merchantId, user.merchantName],
  );
  // Merchant settings are the only configuration that can open the studio tab.
  const merchantSettings = useAtomValue(merchantSettingsAtom);
  const studioCategoryConfiguration = categoryIdentity
    ? getStudioCategoryConfiguration(categoryIdentity, merchantSettings?.cdtStudioConfigurations)
    : undefined;
  const canEditCdt = hasPermission(user.roleCode, PermissionCode.CDT_EDIT);
  const availability = getCdtStudioAvailability({
    isPog,
    merchflowId,
    hasCategoryIdentity: categoryIdentity !== undefined,
    hasCategoryConfiguration: studioCategoryConfiguration !== undefined,
    canEditCdt,
    hasUnsupportedFixtures,
  });

  return { availability, categoryIdentity, studioCategoryConfiguration, canEditCdt };
};
